import { useCallback, useContext, useMemo } from 'react';
import { IssuerAddUpdateBankModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdateBankModal.tsx';
import {
  useIssuerDeleteBankMutation,
  useRegisterBanksListLazyQuery,
} from '@/modules/issuer-dashboard/queries/banks.ts';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DotsHorizontalIcon } from 'ui/icons';
import { fixNameEnum, goodFormatNumber, goodFormatNumberCustom } from 'ui/utils';
import { RegisterBankExtended } from 'api';

type BankTable =
  | 'Bank account'
  | 'Bank type'
  | 'Available cash'
  | 'Currency'
  | 'Portion of portfolio value'
  | '% of register value'
  | '';

const tableHeadItems: TableHeadColumn<BankTable>[] = [
  { title: 'Bank account' },
  { title: 'Bank type' },
  { title: 'Currency' },
  { title: 'Available cash' },
  { title: 'Portion of portfolio value' },
  { title: '% of register value' },
  { title: '' },
];

interface PortfolioBankAccountsTabProps {
  portfolioId?: number;
}
//
export const PortfolioBankAccountsTab = ({ portfolioId }: PortfolioBankAccountsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { isMobile } = useIsMobile();

  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
    data: dataBanksList,
  } = useRegisterBanksListLazyQuery({
    page: 1,
    page_size: 10,
    is_overall: !portfolioId,
    portfolio_id: portfolioId,
  });

  const tableItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataBanksList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataBanksList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(item => {
          const {
            bank_account_name,
            bank_types,
            total_available_cash,
            portion_of_portfolio_value,
            percent_of_portfolio_value,
            bank_currency,
          } = item;

          const currency = bank_currency.toUpperCase();

          const body: BodyCell[] = [
            {
              cell: <div className="text-blue-700">{bank_account_name}</div>,
            },
            {
              cell: <span>{bank_types?.map(item => fixNameEnum(item)).join(', ')}</span>,
            },
            { cell: currency?.toUpperCase() },
            {
              cell: (
                <span>{goodFormatNumberCustom(total_available_cash || 0, currency, 0, 2)}</span>
              ),
            },
            {
              cell: (
                <span>
                  {goodFormatNumberCustom(portion_of_portfolio_value || 0, currency, 0, 2)}
                </span>
              ),
            },
            {
              cell: <span>{goodFormatNumber(percent_of_portfolio_value || 0)}%</span>,
            },
            { cell: <BankMoreButton item={item} portfolioId={portfolioId} /> },
          ];

          return {
            cells: body,
          };
        }),
      );
    });
    return result;
  }, [dataBanksList, portfolioId]);

  const handleOpenAddBankModal = useCallback(() => {
    openModal(IssuerAddUpdateBankModal, {
      portfolioId,
    });
  }, [openModal, portfolioId]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full"></div>

        <div className="flex gap-2">
          <Button className="w-[144px] px-0" onClick={handleOpenAddBankModal}>
            Add bank account
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={tableHeadItems}
        items={tableItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

export interface BankMoreButtonProps {
  item: RegisterBankExtended;
  portfolioId?: number;
}

export const BankMoreButton = ({ item, portfolioId }: BankMoreButtonProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeleteBankMutation();
  const items = useMemo<DropdownMenuItem[]>(() => {
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal(IssuerAddUpdateBankModal, { item, portfolioId });
        },
      },
      {
        label: 'Delete',
        onClick: () => {
          openModal(ConfirmModal, {
            title: <span>Delete</span>,
            onConfirm: async () => {
              await mutateAsyncDelete(item.id);
            },
          });
        },
      },
    ];
  }, [item, mutateAsyncDelete, openModal, portfolioId]);

  return (
    <div>
      <DropdownMenu
        items={items}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};
